import type { ExportableFormat } from '../generated/metapic-api/metapicApi.schemas'

import type { ClientListItem, ClientListItemServer } from './client.types'
import type {
  Filter,
  ProcessableImage,
  ProcessableServerImage,
  RevenueTier,
  RevenueTierServer,
} from './common.types'
import type { Segment } from './segments.types'
import type { SocialItem, SocialItemServer } from './socials.types'
import type { UserTag, UserTagServer } from './user-tag.types'

/**
 * Wire values: advertiser-app sends them as `userStatus` to the legacy `/users/find` endpoint, so
 * they cannot be renamed. The admin UI words these states after `GET /v2/users` in the components.
 */
export enum CreatorStatusEnum {
  NotApproved = 'Not Approved',
  Approved = 'Approved',
  Validated = 'Validated',
  NotValidated = 'Not Validated',
  Banned = 'Banned',
}

export enum CreatorStatusV2Enum {
  Accepted = 'accepted',
  Suspended = 'suspended',
  Undecided = 'undecided',
}

// GET /v2/users endpoint
export type CreatorsV2Filter = {
  client?: Pick<ClientListItem, 'id' | 'name'>
  status?: CreatorStatusV2Enum
  segment?: Pick<Segment, 'id' | 'name'>
  userTags?: Pick<UserTag, 'id' | 'name'>[]
  format?: ExportableFormat
} & Filter

/**
 * A row of the admin Creators list, built from `GET /v2/users`. Deliberately narrower than
 * {@link CreatorListItem}, which the legacy endpoints feed: `/v2/users` carries no revenue share,
 * no revenue-tier client id or timestamps, and no `updated_at`.
 */
export type Creator = {
  id: number
  username: string
  email: string
  firstName: string | null
  lastName: string | null
  createdAt: string | null
  client: Pick<ClientListItem, 'id' | 'name'> | null
  revenueTier: Pick<RevenueTier, 'id' | 'name'> | null
  latestPaymentDate: string | null
  isVerified: boolean
  isSuspended: boolean
  status: CreatorStatusEnum
  avatar?: ProcessableImage
  tags: UserTag[]
  socials: SocialItem[]
  recruitmentUtm: string | null
  recruitmentData?: RecruitmentData
  myPageSlug: string | null
}

// legacy GET /users/find endpoint
export type CreatorsFilter = {
  client?: Pick<ClientListItem, 'id' | 'name'>
  status?: CreatorStatusEnum
  format?: 'xlsx' | 'csv'
} & Filter

// legacy naming is used to match with legacy keys for API
export type CreatorsFilterServer = {
  storeGroup?: number
  legacyClientId?: number
  userStatus: CreatorStatusEnum
} & Omit<CreatorsFilter, 'client'>

export type RecruitmentCampaignServer = {
  id: number
  name: string
  advertiser: {
    id: number
    name: string
  }
}

export type RecruitmentDataServer = {
  campaign?: RecruitmentCampaignServer
}

export type CreatorListItemServer = {
  id: number
  username: string
  email: string
  client: ClientListItemServer
  social_media: SocialItemServer[]
  first_name: string
  surname: string
  revenue_tier: RevenueTierServer
  latest_payment_date?: string
  created_at: string
  updated_at: string
  is_verified: boolean
  is_suspended: boolean
  user_tags: UserTagServer[]
  user_image?: ProcessableServerImage
  recruitment_utm?: string | null
  recruitment_data?: RecruitmentDataServer
  my_page_slug?: string | null
}

export type RecruitmentCampaign = {
  id: number
  name: string
  advertiser: {
    id: number
    name: string
  }
}

export type RecruitmentData = {
  campaign?: RecruitmentCampaign
}

export type CreatorListItem = {
  id: number
  username: string
  email: string
  firstName: string
  lastName: string
  createdAt: string
  updatedAt: string
  client: ClientListItem
  revenueTier: RevenueTier
  latestPaymentDate?: string
  isVerified: boolean
  isSuspended: boolean
  status: CreatorStatusEnum
  avatar?: ProcessableImage
  tags: UserTag[]
  socials: SocialItem[]
  recruitmentUtm?: string | null
  recruitmentData?: RecruitmentData
  myPageSlug?: string | null
}
